// Pure row-building + serialization for the profile-page collection export
// (XLSX + CSV). Zero imports on purpose: everything app-specific (supabase,
// SET_REGISTRY, set files, photo URLs) is injected by the runner in
// collectionExportRun.js, so `node --test src/lib/collectionExport.test.mjs`
// can import this file directly — same workflow as the scan-card reconciler
// and parallelsConfig tests.
//
// card_key grammar (see src/lib/cardKey.js, the canonical parser):
//   <setId>__<subSetId>__<cardN>[__parallel__<parallelId>][__s__<i>]
// The tiny parser below mirrors it 1:1; the grammar is frozen (collector rows
// reference it), so the duplication is safe and keeps this module import-free.

export const EXPORT_COLUMNS = [
  'Set ID', 'Set Name', 'Subset', 'Type', 'Card #', 'Card Name',
  'Artist/Subject', 'Parallel', 'Print Run', 'Serial #',
  'Sketch Subject', 'Sketch Parallel',
  'Status', 'Condition',
  'Grading Company', 'Grade', 'Cert #', 'Graded Serial', 'Graded Print Run',
  'TAG Score', 'Pop This Grade', 'Pop Higher', 'Report URL',
  'Sale Price', 'Date Sold', 'Willing To Pay', 'Trade Value', 'Trade Notes',
  'Photo Front URL', 'Photo Back URL', 'Date Added',
  // Private Notes comes from public.collection_notes, NOT the collections
  // row — the runner attaches it as r.private_note. Card Key stays last:
  // it is the round-trip anchor the import matches on.
  'Private Notes', 'Card Key',
];

function parseKey(cardKey) {
  let key = cardKey;
  let serialNum = null;
  if (key.includes('__s__')) {
    const i = key.lastIndexOf('__s__');
    serialNum = key.slice(i + 5);
    key = key.slice(0, i);
  }
  const parts = key.split('__');
  let setId = '', subSetId = '', cardN = '', parallelId = null;
  const pIdx = parts.indexOf('parallel');
  if (pIdx !== -1) parallelId = parts.slice(pIdx + 1).join('__');
  const kParts = pIdx !== -1 ? parts.slice(0, pIdx) : parts;
  if (kParts.length >= 3) {
    cardN = kParts[kParts.length - 1];
    subSetId = kParts[kParts.length - 2];
    setId = kParts.slice(0, kParts.length - 2).join('__');
  }
  return { setId, subSetId, cardN, parallelId, serialNum };
}

// Natural card-number compare: 2 < 10, ALS-2 < ALS-10.
export function naturalCompare(a, b) {
  const chunk = (s) => String(s).match(/\d+|\D+/g) || [];
  const ca = chunk(a), cb = chunk(b);
  for (let i = 0; i < Math.max(ca.length, cb.length); i++) {
    const x = ca[i], y = cb[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const nx = /^\d+$/.test(x), ny = /^\d+$/.test(y);
    if (nx && ny) { const d = Number(x) - Number(y); if (d) return d; }
    else { const d = x.toLowerCase().localeCompare(y.toLowerCase()); if (d) return d; }
  }
  return 0;
}

// ISO timestamp -> YYYY-MM-DD (blank in, blank out).
const isoDate = (ts) => (ts ? String(ts).slice(0, 10) : '');
const blank = (v) => (v === null || v === undefined ? '' : v);

// Build export rows from raw `collections` rows.
// ctx:
//   setData     { [setId]: { name, shortName, sets: SETS } } — insertion order
//               is registry order (setOrder is the authority for sorting).
//   setOrder    [setId, ...] in SET_REGISTRY order.
//   parallelFor (parallelId) => { name, print_run } | null — orphan-id fallback.
//   getPhotoUrl (cardKey, 'front'|'back') => url | '' — only for objects that exist.
// Returns sorted row objects: { setId, setName, shortName, status, isGraded, cells }.
export function buildCollectionRows(dbRows, ctx) {
  const { setData = {}, setOrder = [], parallelFor, getPhotoUrl } = ctx || {};

  // Parent suppression: a row whose key equals another row's key minus its
  // __s__<i> tail is a phantom parent (mirrors Gallery.buildTilesForCard) —
  // export only the children.
  const parentKeys = new Set();
  for (const r of dbRows) {
    const k = r.card_key || '';
    if (k.includes('__s__')) parentKeys.add(k.slice(0, k.lastIndexOf('__s__')));
  }
  const rows = dbRows.filter((r) => !parentKeys.has(r.card_key));

  const setRank = new Map(setOrder.map((id, i) => [id, i]));

  const built = rows.map((r) => {
    const { setId, subSetId, cardN, parallelId, serialNum } = parseKey(r.card_key || '');
    const sd = setData[setId];
    const sets = sd?.sets || [];
    const subIdx = sets.findIndex((s) => s.id === subSetId);
    const subset = subIdx !== -1 ? sets[subIdx] : null;
    const cardIdx = subset ? (subset.cards || []).findIndex((c) => String(c.n) === cardN) : -1;
    const card = cardIdx !== -1 ? subset.cards[cardIdx] : null;

    // 'base' / empty parallelId is the no-parallel sentinel — never the literal "base".
    let parallelName = '', printRun = '', parRank = -1;
    if (parallelId && parallelId !== 'base') {
      const pIdx = subset ? (subset.parallels || []).findIndex((p) => p.id === parallelId) : -1;
      const p = pIdx !== -1 ? subset.parallels[pIdx]
        : (parallelFor && parallelFor(parallelId)) || { name: parallelId, print_run: null };
      parallelName = blank(p.name);
      printRun = blank(p.print_run);
      parRank = pIdx !== -1 ? pIdx : 9999;
    }

    // __s__<i> is overloaded: after __parallel__ it's a real serial number;
    // with no parallel segment it's a multi-entry copy index (sketches, v2.4.25).
    let serial = '';
    if (serialNum !== null) serial = parallelId ? serialNum : `copy ${serialNum}`;

    const cells = [
      setId,
      sd?.name || '',
      subset ? blank(subset.title) : subSetId,
      subset?.type || '',
      cardN,
      card?.name || '',
      card?.artist || '',
      parallelName,
      printRun,
      serial,
      blank(r.sketch_subject),
      blank(r.sketch_parallel),
      blank(r.status),
      blank(r.card_condition),
      blank(r.grading_company),
      blank(r.grading_grade),
      blank(r.grading_cert_num),
      blank(r.grading_serial_num),
      blank(r.grading_print_run),
      blank(r.grading_tag_score),
      blank(r.grading_pop_this),
      blank(r.grading_pop_higher),
      blank(r.grading_report_url),
      blank(r.sale_price),
      isoDate(r.sold_at),
      blank(r.willing_to_pay),
      blank(r.trade_value),
      blank(r.trade_notes),
      (getPhotoUrl && getPhotoUrl(r.card_key, 'front')) || '',
      (getPhotoUrl && getPhotoUrl(r.card_key, 'back')) || '',
      isoDate(r.created_at),
      blank(r.private_note),
      r.card_key || '',
    ];

    return {
      setId,
      setName: sd?.name || '',
      shortName: sd?.shortName || setId,
      status: r.status || '',
      isGraded: !!r.grading_grade,
      _sort: {
        set: setRank.has(setId) ? setRank.get(setId) : 9999,
        setId,
        sub: subIdx !== -1 ? subIdx : 9999,
        subId: subSetId,
        cardIdx: cardIdx !== -1 ? cardIdx : 9999,
        cardN,
        par: parRank,
        serial: serialNum !== null && /^\d+$/.test(serialNum) ? Number(serialNum) : 0,
      },
      cells,
    };
  });

  built.sort((a, b) => {
    const x = a._sort, y = b._sort;
    return (x.set - y.set)
      || x.setId.localeCompare(y.setId)
      || (x.sub - y.sub)
      || x.subId.localeCompare(y.subId)
      || (x.cardIdx - y.cardIdx)
      || naturalCompare(x.cardN, y.cardN)
      || (x.par - y.par)
      || (x.serial - y.serial);
  });
  return built;
}

// ── CSV ──────────────────────────────────────────────────────────────
// RFC 4180: quote fields containing comma/quote/newline, double inner quotes.
// UTF-8 BOM so Excel detects encoding; CRLF line endings.
export function csvEscape(v) {
  const s = String(v ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows) {
  const lines = [EXPORT_COLUMNS.map(csvEscape).join(',')];
  for (const r of rows) lines.push(r.cells.map(csvEscape).join(','));
  return "\uFEFF" + lines.join('\r\n') + '\r\n';
}

// ── XLSX sheet building ──────────────────────────────────────────────
// Excel tab names: max 31 chars, no [ ] : * ? / \ — dedupe collisions with
// a numeric suffix that still fits the cap.
export function sanitizeTabName(name, used) {
  let base = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, '').trim() || 'Sheet';
  base = base.slice(0, 31);
  let candidate = base, n = 2;
  while (used.has(candidate)) {
    const suffix = ` ${n}`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
    n++;
  }
  used.add(candidate);
  return candidate;
}

export const SUMMARY_COLUMNS = ['Set Name', 'Collected', 'Wishlist', 'For Sale', 'Trade', 'Graded', 'Total'];

// Group sorted rows by set -> Summary aoa + one aoa per set (registry order is
// preserved because `rows` is already sorted). Counts use the same
// parent-suppressed rows as the tabs, not raw DB counts.
export function toWorkbookSheets(rows) {
  const groups = [];
  const byId = new Map();
  for (const r of rows) {
    let g = byId.get(r.setId);
    if (!g) { g = { setId: r.setId, setName: r.setName, shortName: r.shortName, rows: [] }; byId.set(r.setId, g); groups.push(g); }
    g.rows.push(r);
  }

  const summaryAoa = [SUMMARY_COLUMNS];
  for (const g of groups) {
    const count = (s) => g.rows.filter((r) => r.status === s).length;
    summaryAoa.push([
      g.setName || g.setId,
      count('collected'), count('wishlist'), count('for_sale'), count('trade'),
      g.rows.filter((r) => r.isGraded).length,
      g.rows.length,
    ]);
  }

  const used = new Set(['Summary']);
  const sheets = [{ name: 'Summary', aoa: summaryAoa }];
  for (const g of groups) {
    sheets.push({
      name: sanitizeTabName(g.shortName, used),
      aoa: [EXPORT_COLUMNS, ...g.rows.map((r) => r.cells)],
    });
  }
  return sheets;
}

// Column widths for a sheet: header length vs longest value, capped sane.
export function sheetColWidths(aoa) {
  const widths = (aoa[0] || []).map((h) => String(h).length);
  for (const row of aoa.slice(1)) {
    row.forEach((v, i) => { widths[i] = Math.min(60, Math.max(widths[i] || 8, String(v ?? '').length)); });
  }
  return widths.map((wch) => ({ wch: wch + 2 }));
}

export function exportFilename(username, ext, now) {
  const d = now instanceof Date ? now : new Date();
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const who = (username || '').trim().replace(/[^a-zA-Z0-9_-]/g, '') || 'collection';
  return `collectionkeeper_${who}_${ymd}.${ext}`;
}
